import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';
import { appConfig } from '@/app/config';

// Firebase configuration (env reads live in app/config.ts — the only place
// in the app that reads environment variables)
const firebaseConfig = appConfig.firebase;

// Only initialize Firebase when it is actually configured (e.g. env vars
// are absent in local dev or CI). Downstream consumers must tolerate null.
const isFirebaseConfigured = Boolean(firebaseConfig.apiKey);

export const firebaseApp: FirebaseApp | null = isFirebaseConfigured
  ? initializeApp(firebaseConfig)
  : null;

export const db: Firestore | null = firebaseApp
  ? getFirestore(firebaseApp)
  : null;

// Firebase Analytics sends GA4 automatic `page_view` events that the SDK cannot
// disable; the app's own `screen_view` events (firebase-analytics.ts) are the
// ones that carry bot-detection metadata, so treat `page_view` counts as noisy.
//
// `.catch(() => null)` is deliberate: isSupported()/getAnalytics failures
// (blocked by an ad blocker, unsupported browser, measurement ID rejected) must
// degrade to "no analytics" and never surface as an unhandled rejection.
// Analytics is not allowed to break the app.
export const analytics: Promise<Analytics | null> | null =
  firebaseApp && typeof window !== 'undefined'
    ? isSupported()
        .then((yes) => (yes ? getAnalytics(firebaseApp) : null))
        .catch(() => null)
    : null;
